import React, { useEffect, useReducer, useState } from 'react';
import initialGreaseFillReducer, {
	initialInitialGreaseFillState,
} from './reducer';
import { greaseFillOption, text } from '../../data';
import FillByPartNumber from './fill-by-part-number/FillByPartNumber';
import {
	BearingCalculationData,
	GreaseQuantityPayload,
} from '../../../components/bearing-search/types/types';
import {
	getGreaseQuantity,
	greaseLubricationContent,
} from '../../../components/bearing-search/api/api';
import CalculationResults from '../../components/CalculationResults';
import FillBySpecification from './fill-by-specification/FillBySpecification';
import { toggleNextTools } from '../../utils/engineering-tools';
import { trackResults, trackStart, updateActionParams } from '../../utils/analytics';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';

export type GreaseQuantityPartPayload = Omit<
	GreaseQuantityPayload,
	'isDBRetrieval' | 'isReportRequired'
>;

const InitialGreaseFill: React.FC = () => {
	const formDescription = greaseLubricationContent?.initialGreaseFillDesc
		? { __html: greaseLubricationContent.initialGreaseFillDesc }
		: undefined;

	const footerNote = greaseLubricationContent?.initialGreaseFillFooter
		? { __html: greaseLubricationContent.initialGreaseFillFooter }
		: undefined;

	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ errorMessage, setErrorMessage ] = useState<string>( '' );
	const [ formState, dispatch ] = useReducer(
		initialGreaseFillReducer,
		initialInitialGreaseFillState,
	);

	useEffect( () => {
		trackStart( 'initialGreaseFill' );
	}, [] );

	// Show / hide recommended tools
	useEffect( () => {
		toggleNextTools( isCalculated && ! isResultsLoading );

		return () => {
			toggleNextTools( false );
		};
	}, [ isCalculated, isResultsLoading ] );

	const handleSubmit = (
		requestData: GreaseQuantityPartPayload,
		bearing?: BearingPartState,
	) => {
		const request: GreaseQuantityPayload = {
			...requestData,
			isDBRetrieval: formState.option === 'PartNumber',
			isReportRequired: true,
		};

		setIsCalculated( true );
		setIsResultsLoading( true );
		setErrorMessage( '' );

		getGreaseQuantity( request )
			.then( ( response ) => {
				updateActionParams( 'initialGreaseFill', bearing );

				let reportData: Array<BearingCalculationData | null> = [];

				if ( response.report && response.report.length > 0 ) {
					reportData = response.report[ 0 ].data;
				}

				setResults( reportData );
				trackResults( 'initialGreaseFill', request, reportData.length, bearing?.marketPlaceDescription );

				if ( response.header.messages?.length ) {
					setErrorMessage( response.header.messages[ 0 ].messageText );
				}

				document.querySelector('.t-engineering-tools__results').scrollIntoView({
					behavior: 'smooth'
				});

			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsCalculated( false );
		setErrorMessage( '' );
	};

	return (
		<div>
			{!! formDescription && (
				<p
					className="t-engineering-tools__description"
					dangerouslySetInnerHTML={formDescription}
				></p>
			)}

			<p>
				<span className="c-form__asterisk">*</span>
				{text.requiredMessage}
			</p>

			<div className="t-engineering-tools__options">
				{greaseFillOption.map( ( option ) => {
					return (
						<div className="m-radio" key={option.id}>
							<div className="m-radio__field">
								<input
									id={option.id}
									className="m-radio__input"
									checked={formState.option === option.value}
									type="radio"
									value={option.value}
									onChange={() =>
										dispatch( { type: 'change_option', payload: option.value } )
									}
								/>
								<label className="m-radio__label" htmlFor={option.id}>
									{option.label}
								</label>
							</div>
						</div>
					);
				} )}
			</div>

			{formState.option === 'PartNumber' && (
				<FillByPartNumber onSubmit={handleSubmit} onClear={clearSearch} />
			)}

			{formState.option === 'Specification' && (
				<FillBySpecification onSubmit={handleSubmit} onClear={clearSearch} />
			)}

			{isCalculated && (
				<CalculationResults data={results} loading={isResultsLoading} errorMessage={errorMessage} />
			)}

			{isCalculated && ! isResultsLoading && footerNote && (
				<div
					className="t-engineering-tools__notes"
					dangerouslySetInnerHTML={footerNote}
				></div>
			)}
		</div>
	);
};

export default InitialGreaseFill;
